import { cap, redactText } from "./redact";

/** The spot the person clicked, in viewport pixels, and the element under it. */
export interface PinPoint {
  x: number;
  y: number;
}

export interface ElementInfo {
  selector: string;
  tag: string;
  role?: string;
  label?: string;
  text?: string;
}

export interface ScreenshotInfo {
  captured: boolean;
  error?: string;
  width?: number;
  height?: number;
  bytes?: number;
}

export interface ScreenshotResult {
  blob: Blob | null;
  info: ScreenshotInfo;
}

/** Everything that belongs to the feedback UI itself carries this attribute and is left out of the screenshot. */
export const FEEDBACK_UI_ATTRIBUTE = "data-feedback-ui";

export const SCREENSHOT_TARGET_BYTES = 2 * 1024 * 1024;
export const SCREENSHOT_TIMEOUT_MS = 10_000;

const IMPLICIT_ROLES: Record<string, string> = {
  a: "link",
  button: "button",
  input: "textbox",
  select: "combobox",
  textarea: "textbox",
  table: "table",
  nav: "navigation",
  main: "main",
  header: "banner",
  dialog: "dialog",
  h1: "heading",
  h2: "heading",
  h3: "heading",
};

function segment(el: Element): string {
  const tag = el.tagName.toLowerCase();
  if (el.id && /^[A-Za-z][\w-]*$/.test(el.id)) return `${tag}#${el.id}`;
  const classes = [...el.classList].filter((c) => /^[A-Za-z][\w-]*$/.test(c)).slice(0, 2);
  let part = tag + classes.map((c) => `.${c}`).join("");
  const parent = el.parentElement;
  if (parent) {
    const same = [...parent.children].filter((child) => child.tagName === el.tagName);
    if (same.length > 1) part += `:nth-of-type(${same.indexOf(el) + 1})`;
  }
  return part;
}

/** A CSS selector path (at most 8 levels, stopping at an element with an id), for the engineer to find the element again. */
export function selectorPath(el: Element): string {
  const parts: string[] = [];
  let node: Element | null = el;
  while (node && node.tagName.toLowerCase() !== "html" && parts.length < 8) {
    const part = segment(node);
    parts.unshift(part);
    if (part.includes("#") || node.tagName.toLowerCase() === "body") break;
    node = node.parentElement;
  }
  return parts.join(" > ");
}

function labelOf(el: Element): string | undefined {
  const aria = el.getAttribute("aria-label");
  if (aria) return aria;
  const labelledBy = el.getAttribute("aria-labelledby");
  if (labelledBy) {
    const text = labelledBy
      .split(/\s+/)
      .map((id) => el.ownerDocument.getElementById(id)?.textContent?.trim())
      .filter(Boolean)
      .join(" ");
    if (text) return text;
  }
  const title = el.getAttribute("title");
  if (title) return title;
  const label = el.closest("label");
  return label?.textContent?.trim() || undefined;
}

/** What the person clicked, in words. Never an input's value. */
export function describeElement(el: Element): ElementInfo {
  const tag = el.tagName.toLowerCase();
  const role = el.getAttribute("role") ?? IMPLICIT_ROLES[tag];
  const label = labelOf(el);
  const isField = tag === "input" || tag === "textarea" || tag === "select";
  const rawText = isField ? "" : (el.textContent ?? "").replace(/\s+/g, " ").trim();
  return {
    selector: cap(selectorPath(el), 600),
    tag,
    ...(role ? { role } : {}),
    ...(label ? { label: redactText(cap(label, 120), 160) } : {}),
    ...(rawText ? { text: redactText(cap(rawText, 160), 200) } : {}),
  };
}

/** The element under a viewport point, ignoring the feedback UI; the body when the browser cannot tell. */
export function elementAt(point: PinPoint, doc: Document = document): Element {
  const all = typeof doc.elementsFromPoint === "function" ? doc.elementsFromPoint(point.x, point.y) : [];
  const hit = all.find((el) => !el.closest(`[${FEEDBACK_UI_ATTRIBUTE}]`));
  return hit ?? doc.body;
}

function withTimeout<T>(promise: Promise<T>, ms: number, what: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${what} took longer than ${Math.round(ms / 1000)} s`)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error instanceof Error ? error : new Error(failureMessage(error)));
      },
    );
  });
}

function toBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("the browser could not encode the image"))), "image/png");
  });
}

function drawPin(canvas: HTMLCanvasElement, point: PinPoint, scale: number): void {
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("drawing is not available in this browser");
  const x = point.x * scale;
  const y = point.y * scale;
  const r = 11 * scale;
  ctx.save();
  ctx.beginPath();
  ctx.arc(x, y, r + 3 * scale, 0, Math.PI * 2);
  ctx.fillStyle = "#ffffff";
  ctx.fill();
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fillStyle = "#c62828";
  ctx.fill();
  ctx.beginPath();
  ctx.arc(x, y, r / 3, 0, Math.PI * 2);
  ctx.fillStyle = "#ffffff";
  ctx.fill();
  ctx.restore();
}

function shrink(canvas: HTMLCanvasElement, factor: number): HTMLCanvasElement {
  const next = document.createElement("canvas");
  next.width = Math.max(1, Math.round(canvas.width * factor));
  next.height = Math.max(1, Math.round(canvas.height * factor));
  const ctx = next.getContext("2d");
  if (!ctx) throw new Error("drawing is not available in this browser");
  ctx.drawImage(canvas, 0, 0, next.width, next.height);
  return next;
}

/**
 * What stands in for a picture the screenshot cannot copy (NEW-A, batch 16). The library re-reads every picture on the page
 * to copy it; an attachment link answers with a redirect to the storage host, which does not allow that read (no CORS), so
 * the picture cannot be copied. Instead of failing the whole screenshot, such a picture becomes this neutral box at the
 * picture's own size, so the layout around it stays true. A `data:` image: allowed by the page's CSP (img-src data:), so
 * nothing about the policy is loosened. Sized by the picture's box (no viewBox), so the words never stretch.
 */
export const PICTURE_PLACEHOLDER = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%"><rect width="100%" height="100%" fill="#e9edf2" stroke="#9aa5b4" stroke-width="2" stroke-dasharray="6 4"/><text x="50%" y="50%" fill="#4a5568" font-family="sans-serif" font-size="13" text-anchor="middle" dominant-baseline="middle">Picture (not copied into the screenshot)</text></svg>',
)}`;

/**
 * A failure in words. The library rejects with a bare DOM Event when a picture or style will not load, which once reached
 * the report as "[object Event]".
 */
export function failureMessage(error: unknown): string {
  if (error instanceof Error) return error.message || "unknown error";
  if (typeof Event !== "undefined" && error instanceof Event) {
    const target = error.target;
    const what = target instanceof HTMLImageElement ? "a picture" : target instanceof Element ? `a ${target.tagName.toLowerCase()} element` : "a picture or style";
    return `${what} on the page could not be loaded (${error.type || "error"})`;
  }
  const text = String(error);
  return text === "[object Object]" || !text ? "unknown error" : text;
}

/** The cloned body's style that brings the scrolled-to part of the page into the screenshot's frame. */
export function visibleArea(scrollX: number, scrollY: number): Partial<CSSStyleDeclaration> | undefined {
  if (!scrollX && !scrollY) return undefined;
  return { transform: `translate(${-Math.round(scrollX)}px, ${-Math.round(scrollY)}px)`, transformOrigin: "0 0" };
}

/**
 * The SVG painting properties copied onto every element inside an `<svg>` for the screenshot (GRF-81). The library copies each
 * HTML element's computed style into its copy, but clones an `<svg>` whole without visiting what is inside it, and the copy is
 * drawn without the page's stylesheets. So anything inside an SVG painted by a class rule (the workflow diagram: background,
 * boxes, arrows, labels) fell back to SVG's defaults, `fill: black` — the diagram's full-size background rect turned the whole
 * diagram into a black box.
 */
export const SVG_PAINT_PROPERTIES = [
  "fill",
  "fill-opacity",
  "fill-rule",
  "stroke",
  "stroke-width",
  "stroke-dasharray",
  "stroke-dashoffset",
  "stroke-linecap",
  "stroke-linejoin",
  "stroke-opacity",
  "opacity",
  "visibility",
  "display",
  "color",
  "font-family",
  "font-size",
  "font-weight",
  "font-style",
  "letter-spacing",
  "text-anchor",
  "dominant-baseline",
  "paint-order",
] as const;

/**
 * Writes each SVG descendant's computed painting style (SVG_PAINT_PROPERTIES) into its inline style, so the library's whole-SVG
 * clone carries it, and returns the function that puts every touched element's `style` attribute back exactly as it was
 * (idempotent; safe to call twice). Nothing visible changes in the meantime: the inline values ARE the computed ones. Elements
 * for which `skip` is true (the feedback UI) are left alone.
 */
export function inlineSvgPaint(root: ParentNode, skip: (el: Element) => boolean = () => false): () => void {
  const touched: { el: SVGElement; style: string | null }[] = [];
  for (const svg of Array.from(root.querySelectorAll("svg"))) {
    if (skip(svg)) continue;
    for (const el of Array.from(svg.querySelectorAll("*"))) {
      if (!(el instanceof SVGElement)) continue;
      const computed = window.getComputedStyle(el);
      const before = el.getAttribute("style");
      let changed = false;
      for (const name of SVG_PAINT_PROPERTIES) {
        const value = computed.getPropertyValue(name);
        if (!value || el.style.getPropertyValue(name)) continue;
        if (!changed) touched.push({ el, style: before });
        changed = true;
        el.style.setProperty(name, value);
      }
    }
  }
  let restored = false;
  return () => {
    if (restored) return;
    restored = true;
    for (const { el, style } of touched) {
      if (style === null) el.removeAttribute("style");
      else el.setAttribute("style", style);
    }
  };
}

/**
 * The visible page as a PNG with the pin drawn at the clicked spot, the feedback UI left out. Never throws: a failure
 * (library, CSS the library cannot render, a timeout, a picture that will not fit under `maxBytes`) comes back as
 * `{ blob: null, info: { captured: false, error } }` so the report still goes out without it.
 */
export async function captureScreenshot(point: PinPoint, maxBytes = SCREENSHOT_TARGET_BYTES, timeoutMs = SCREENSHOT_TIMEOUT_MS): Promise<ScreenshotResult> {
  let restoreSvgPaint = () => {};
  try {
    const work = (async () => {
      const { toCanvas } = await import("html-to-image");
      const width = window.innerWidth;
      const height = window.innerHeight;
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 1.5);
      // GRF-81: SVG contents keep their class-painted colours in the copy (see inlineSvgPaint); put back once drawn.
      restoreSvgPaint = inlineSvgPaint(document.body, (el) => el.closest(`[${FEEDBACK_UI_ATTRIBUTE}]`) !== null);
      let canvas = await toCanvas(document.body, {
        width,
        height,
        pixelRatio,
        backgroundColor: "#ffffff",
        // The library draws the body from its top-left corner, ignoring the window's scroll. The pin is in viewport
        // coordinates, so a scrolled page used to come back as its top with the pin on the wrong thing: shift the copy
        // by the scroll so the screenshot is the part the person sees. (A sticky header stays at the top of the copy,
        // so on a scrolled page it is not in the screenshot; the content under it is.)
        style: visibleArea(window.scrollX, window.scrollY),
        filter: (node: HTMLElement) => !(node instanceof Element && node.hasAttribute(FEEDBACK_UI_ATTRIBUTE)),
        // A picture that cannot be read (an attachment's cross-origin storage redirect) becomes the placeholder box.
        imagePlaceholder: PICTURE_PLACEHOLDER,
        // A picture that still will not decode (a corrupt file) is left blank instead of failing the screenshot.
        onImageErrorHandler: () => undefined,
      }).finally(() => restoreSvgPaint());
      drawPin(canvas, point, canvas.width / Math.max(1, width));
      let blob = await toBlob(canvas);
      for (let attempt = 0; blob.size > maxBytes && attempt < 5; attempt++) {
        canvas = shrink(canvas, 0.7);
        blob = await toBlob(canvas);
      }
      if (blob.size > maxBytes) throw new Error("the picture is too large even when shrunk");
      return { blob, info: { captured: true, width: canvas.width, height: canvas.height, bytes: blob.size } } satisfies ScreenshotResult;
    })();
    return await withTimeout(work, timeoutMs, "The screenshot");
  } catch (error) {
    return { blob: null, info: { captured: false, error: redactText(cap(failureMessage(error), 300)) } };
  } finally {
    // A timeout returns before the library finishes: the page's own styles come back now either way.
    restoreSvgPaint();
  }
}
